import { WaqLink } from "../../../ui/waq/WaqLink";
import { GlowBlob } from "../../../ui/waq/GlowBlob";
import { gates, gatesPanel, hero, type Gate } from "../../../../content/waq/home";

/** design/Homepage.dc.html lines 67-128. */
export function Hero() {
  return (
    <section className="relative">
      <GlowBlob
        position={{ top: -230, right: -170 }}
        size={840}
        alpha={0.42}
        falloff="62%"
        animated
      />
      <GlowBlob
        position={{ bottom: -340, left: -210 }}
        size={780}
        color="warm"
        alpha={0.18}
        falloff="64%"
      />

      <div className="relative mx-auto grid max-w-container grid-cols-[repeat(auto-fit,minmax(min(440px,100%),1fr))] items-center gap-hero px-section-x py-hero-y">
        <div className="motion-safe:animate-rise">
          <p className="mb-6 inline-flex items-center gap-2.5 rounded-pill border border-blue/45 px-4 py-2 text-eyebrow font-extrabold uppercase tracking-[.14em] text-blue">
            <span
              aria-hidden="true"
              className="h-[7px] w-[7px] rounded-full bg-blue motion-safe:animate-pulse"
            />
            {hero.badge}
          </p>

          <h1 className="mb-6 text-h1 font-extrabold">
            {hero.headingLead}
            <span className="text-yellow">{hero.headingAccent}</span>
          </h1>

          <p className="mb-[34px] max-w-[560px] text-lead text-ondark-body">{hero.lead}</p>

          <div className="flex flex-wrap items-center gap-3.5">
            <WaqLink
              href={hero.primaryCta.href}
              className="rounded-btn bg-yellow px-[30px] py-[17px] text-btn font-bold text-navy"
            >
              {hero.primaryCta.label}
            </WaqLink>
            <WaqLink
              href={hero.secondaryCta.href}
              className="flex items-center gap-2.5 rounded-btn border-[1.5px] border-white/30 px-[26px] py-[17px] text-btn font-semibold text-surface hover:text-surface"
            >
              {hero.secondaryCta.label}{" "}
              <span aria-hidden="true" className="text-blue">
                →
              </span>
            </WaqLink>
          </div>
        </div>

        <div className="motion-safe:animate-rise-delayed">
          <GatesPanel />
        </div>
      </div>
    </section>
  );
}

function GatesPanel() {
  return (
    <div className="relative overflow-hidden rounded-feature border border-ondark-border bg-ondark-card shadow-hero-panel backdrop-blur-[10px]">
      <div className="relative">
        <div className="flex items-center justify-between border-b border-ondark-border px-[22px] py-4">
          <span className="flex items-center gap-[9px] text-[13.5px] font-semibold">
            <span
              aria-hidden="true"
              className="h-2 w-2 rounded-full bg-blue motion-safe:animate-pulse-mid"
            />
            {gatesPanel.release}
          </span>
          <span className="text-micro font-extrabold uppercase text-yellow">
            {gatesPanel.label}
          </span>
        </div>

        <ul className="list-none py-2">
          {gates.map((gate) => (
            <GateRow key={gate.label} gate={gate} />
          ))}
        </ul>

        <div className="flex items-center justify-between gap-4 border-t border-ondark-border px-[22px] py-[15px] text-[12.5px] text-white/65">
          <span>{gatesPanel.footerLeft}</span>
          <span className="font-semibold text-yellow">{gatesPanel.footerRight}</span>
        </div>
      </div>
    </div>
  );
}

function GateRow({ gate }: { gate: Gate }) {
  const passed = gate.state === "passed";
  const active = gate.state === "active";

  return (
    <li
      className={`flex items-center gap-3.5 px-[22px] py-[13px] ${
        active ? "bg-blue/[.16]" : ""
      } ${gate.state === "queued" ? "opacity-[.42]" : ""}`}
    >
      {passed && (
        <span
          aria-hidden="true"
          style={{ "--pop-delay": gate.popDelay } as React.CSSProperties}
          className="flex h-5 w-5 flex-none items-center justify-center rounded-md bg-yellow text-[11px] font-extrabold text-navy motion-safe:animate-pop"
        >
          ✓
        </span>
      )}
      {active && (
        <span
          aria-hidden="true"
          className="flex h-5 w-5 flex-none items-center justify-center rounded-md border-2 border-blue"
        >
          <span className="h-[7px] w-[7px] rounded-full bg-blue motion-safe:animate-pulse-fast" />
        </span>
      )}
      {gate.state === "queued" && (
        <span
          aria-hidden="true"
          className="flex h-5 w-5 flex-none rounded-md border-[1.5px] border-dashed border-white/55"
        />
      )}

      <span
        className={`w-[174px] flex-none text-sm ${
          active ? "font-bold text-blue" : "font-medium"
        }`}
      >
        {gate.label}
      </span>

      <span className="relative h-[5px] flex-1 overflow-hidden rounded-[3px] bg-white/15">
        {passed && (
          <span
            style={
              {
                "--bar-dur": gate.barDuration,
                "--bar-delay": gate.barDelay,
              } as React.CSSProperties
            }
            className="absolute inset-0 w-full bg-yellow motion-safe:animate-fillbar"
          />
        )}
        {active && (
          <span className="absolute inset-0 w-full bg-blue motion-safe:animate-fillbar-live" />
        )}
      </span>

      <span
        className={`w-12 flex-none text-right text-[12.5px] tabular-nums ${
          active ? "font-bold text-blue" : "text-white/60"
        }`}
      >
        {gate.count}
      </span>
    </li>
  );
}
